import CommonChip from '@app/src/components/CommonChip'
import { RiskStatus } from '@app/src/types/resourceExplorer'
import { getRiskColor, getRiskStatusTranslationKey } from '@app/src/utils/riskStatusHelpers'
import { alpha, darken, Typography } from '@mui/material'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import type { LinkProps as RouterLinkProps } from 'react-router-dom'

interface SimpleRiskChipProps {
  riskStatus?: RiskStatus
  disabled?: boolean
  showBackgroundForInactive?: boolean
  to?: RouterLinkProps['to']
  larger?: boolean
  includeRiskWord?: boolean
  outlined?: boolean
  tooltip?: React.ReactNode
}

const getRiskTextColor = (risk?: RiskStatus, disabled?: boolean): string => {
  if (disabled) {
    return 'text.disabled'
  }

  switch (risk) {
    case RiskStatus.NotApplicable:
    case RiskStatus.Deactivated:
    case RiskStatus.Pending:
    case RiskStatus.InsufficientData:
      return 'text.disabled'
    case RiskStatus.Extreme:
      return 'primary.contrastText'
    default:
      return 'primary.main'
  }
}

const SimpleRiskChip: React.FC<SimpleRiskChipProps> = ({
  riskStatus,
  disabled,
  showBackgroundForInactive,
  to,
  larger,
  includeRiskWord = false,
  outlined = false,
  tooltip,
}) => {
  const { formatMessage } = useIntl()
  const isDisabledChip =
    (outlined && riskStatus === RiskStatus.NotApplicable) ||
    riskStatus === RiskStatus.Deactivated ||
    riskStatus === RiskStatus.Pending ||
    riskStatus === RiskStatus.InsufficientData

  const bgColor = useMemo(() => {
    if (disabled) return alpha('#000', 0.04)
    if (outlined) return 'transparent'
    const riskColor = getRiskColor(riskStatus)
    // If transparent and showBackgroundForInactive is true, show gray background
    if (riskColor === 'transparent' && showBackgroundForInactive) {
      return alpha('#000', 0.04)
    }
    return riskColor
  }, [riskStatus, disabled, showBackgroundForInactive, outlined])

  const textColor = useMemo(() => {
    if (outlined && !isDisabledChip) {
      // When outlined is true, keep default text color (not the risk color)
      return 'primary.main'
    }
    return getRiskTextColor(riskStatus, disabled)
  }, [riskStatus, disabled, outlined, isDisabledChip])

  // MUI's clickable Chip hovers to a grey of its own, which wipes out the risk colour. Darken the
  // chip's own background instead.
  const hoverBgColor = useMemo(() => {
    if (!to) return undefined
    return bgColor === 'transparent' ? alpha('#000', 0.04) : darken(bgColor, 0.12)
  }, [to, bgColor])

  const borderColor = useMemo(() => {
    if (isDisabledChip) {
      return alpha('#000', 0.12)
    } else if (outlined) {
      return getRiskColor(riskStatus)
    }
    return undefined
  }, [riskStatus, outlined, isDisabledChip])

  const label = useMemo(() => {
    if (!riskStatus) return formatMessage({ id: 'schemas.risk.notAvailable' })

    if (includeRiskWord) {
      // Long form: "Extreme risk", "High risk", etc.
      return formatMessage({ id: `schemas.risk.${riskStatus}` })
    } else {
      // Short form: "Extreme", "High", etc.
      return formatMessage({ id: getRiskStatusTranslationKey(riskStatus) })
    }
  }, [riskStatus, includeRiskWord, formatMessage])

  if (!riskStatus) {
    return (
      <Typography variant="body2" color="text.disabled">
        {formatMessage({ id: 'companyProfile.inactiveIndex' })}
      </Typography>
    )
  }

  return (
    <CommonChip
      label={label}
      tooltip={tooltip}
      disabled={disabled}
      to={to}
      larger={larger}
      sx={{
        fontWeight:
          riskStatus === RiskStatus.NotApplicable ||
          riskStatus === RiskStatus.Deactivated ||
          riskStatus === RiskStatus.InsufficientData
            ? 400
            : undefined,
        color: textColor,
        backgroundColor: bgColor,
        ...(outlined &&
          borderColor && {
            border: `1px solid ${borderColor}`,
          }),
        ...(hoverBgColor && {
          '&:hover, &.Mui-focusVisible': {
            backgroundColor: hoverBgColor,
          },
        }),
      }}
    />
  )
}

export default SimpleRiskChip
